/**
 * WebSocketStatus - Connection status indicator component
 * Shows real-time connection status with visual feedback
 */

import React from 'react';
import { Wifi, WifiOff, Loader2 } from './icons/CustomDashboardIcons';
import { ConnectionState } from '../hooks/useWebSocket';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export function WebSocketStatus({ connectionState, onlineCount = 0 }) {
  const getStatusConfig = () => {
    switch (connectionState) {
      case ConnectionState.CONNECTED:
        return {
          icon: Wifi,
          color: 'text-emerald-500',
          bgColor: 'bg-emerald-500/10',
          borderColor: 'border-emerald-500/30',
          pulseColor: 'bg-emerald-500',
          label: 'Connected',
          description: `Live connection active${onlineCount > 0 ? ` • ${onlineCount} online` : ''}`
        };
      case ConnectionState.CONNECTING:
        return {
          icon: Loader2,
          color: 'text-amber-500',
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-500/30',
          pulseColor: 'bg-amber-500',
          label: 'Connecting',
          description: 'Establishing connection...',
          spin: true
        };
      case ConnectionState.RECONNECTING:
        return {
          icon: Loader2,
          color: 'text-amber-500',
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-500/30',
          pulseColor: 'bg-amber-500',
          label: 'Reconnecting',
          description: 'Connection lost, reconnecting...',
          spin: true
        };
      case ConnectionState.ERROR:
        return {
          icon: WifiOff,
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          pulseColor: 'bg-red-500',
          label: 'Error',
          description: 'Connection error'
        };
      default:
        return {
          icon: WifiOff,
          color: 'text-stone-500',
          bgColor: 'bg-stone-500/10',
          borderColor: 'border-stone-500/30',
          pulseColor: 'bg-stone-500',
          label: 'Offline',
          description: 'Not connected'
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${config.bgColor} ${config.borderColor} transition-all duration-300`}
            data-testid="websocket-status"
          >
            {/* Pulse indicator */}
            <span className="relative flex h-2 w-2">
              {connectionState === ConnectionState.CONNECTED && (
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.pulseColor} opacity-75`}></span>
              )}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${config.pulseColor}`}></span>
            </span>
            
            {/* Icon */}
            <Icon className={`w-4 h-4 ${config.color} ${config.spin ? 'animate-spin' : ''}`} />
            
            {/* Label */}
            <span className={`text-xs font-medium ${config.color}`}>
              {config.label}
            </span>
            
            {/* Online count badge */}
            {connectionState === ConnectionState.CONNECTED && onlineCount > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] bg-emerald-500/20 text-emerald-400 border-none">
                {onlineCount}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-stone-900 border-stone-700">
          <p className="text-xs">{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Compact version for header/nav
export function WebSocketStatusCompact({ connectionState }) {
  const isConnected = connectionState === ConnectionState.CONNECTED;
  const isConnecting = connectionState === ConnectionState.CONNECTING || connectionState === ConnectionState.RECONNECTING;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative" data-testid="websocket-status-compact">
            {isConnecting ? (
              <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
            ) : isConnected ? (
              <>
                <Wifi className="w-4 h-4 text-emerald-500" />
                <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
              </>
            ) : (
              <WifiOff className="w-4 h-4 text-stone-500" />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-stone-900 border-stone-700">
          <p className="text-xs">
            {isConnecting ? 'Connecting...' : isConnected ? 'Live connected' : 'Offline'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default WebSocketStatus;
